/* ================================================================
   hablar-en-publico-landing.js
   CSS Scroll Snap controller + per-section GSAP animations
   Requires: GSAP 3 (loaded before this script)
   ================================================================ */

(function () {
  'use strict';

  /* ── CONFIG ─────────────────────────────────────────────── */
  const CFG = { totalSections: 13 };

  /* ── BACKGROUND MAP: section index → background index ──── */
  const BG_MAP = [0, 0, 0, 1, 1, 2, 2, 2, 2, 3, 3, 3, 4];

  /* ── GSAP ───────────────────────────────────────────────── */
  gsap.defaults({ overwrite: 'auto' });

  /* ── STATE ──────────────────────────────────────────────── */
  const state = { current: 0 };

  /* ── DOM ─────────────────────────────────────────────────── */
  const els = {
    wrapper : null,
    track   : null,
    sections: null,
    dots    : null,
    arrowDown: null,
    bgs     : null,
  };

  /* ── SECTION ANNOUNCER ──────────────────────────────────── */
  let _visibleSection = 0;

  function _announceSection(toIndex) {
    if (toIndex === _visibleSection) return;
    const from = _visibleSection;
    const dir  = toIndex > from ? 1 : -1;

    document.dispatchEvent(new CustomEvent('hep:sectionLeave', {
      detail: { from, to: toIndex, direction: dir }
    }));

    _visibleSection = toIndex;

    requestAnimationFrame(() => {
      document.dispatchEvent(new CustomEvent('hep:sectionEnter', {
        detail: { index: toIndex, from, direction: dir }
      }));
    });
  }

  /* ── INIT ───────────────────────────────────────────────── */
  function init() {
    els.wrapper  = document.getElementById('hepWrapper');
    els.track    = document.getElementById('hepTrack');
    els.sections = document.querySelectorAll('.hep-section');
    els.dots     = document.querySelectorAll('.hep-dots__dot');
    els.arrowDown = document.getElementById('hepArrowDown');
    els.bgs      = document.querySelectorAll('.hep-bg');

    if (!els.track) return;

    document.body.classList.add('hep-active');

    setupObservers();
    bindDots();
    bindArrow();
    bindKeyboard();

    window.addEventListener('beforeunload', () => {
      document.body.classList.remove('hep-active');
    });

    updateUI(0);
  }

  /* ── OBSERVERS ──────────────────────────────────────────── */
  function setupObservers() {
    const ratios     = new Map();
    const thresholds = Array.from({ length: 21 }, (_, i) => i / 20);

    const mainObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const idx = parseInt(entry.target.dataset.hepIndex, 10);
        ratios.set(idx, entry.intersectionRatio);
      });

      const currentRatio = ratios.get(state.current) || 0;
      let bestIndex = state.current;
      let bestRatio = currentRatio;

      for (const [idx, ratio] of ratios) {
        if (ratio > bestRatio) {
          bestRatio = ratio;
          bestIndex = idx;
        }
      }

      if (bestIndex !== state.current) {
        _announceSection(bestIndex);
        state.current = bestIndex;
        updateUI(bestIndex);
      }
    }, {
      root: els.track,
      threshold: thresholds,
    });

    const cleanupObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          const idx = parseInt(entry.target.dataset.hepIndex, 10);
          document.dispatchEvent(new CustomEvent('hep:sectionHidden', {
            detail: { index: idx }
          }));
        }
      });
    }, {
      root: els.track,
      threshold: 0,
    });

    els.sections.forEach(section => {
      mainObserver.observe(section);
      cleanupObserver.observe(section);
    });
  }

  /* ── GO TO ──────────────────────────────────────────────── */
  function goTo(index) {
    if (index < 0 || index >= CFG.totalSections) return;
    const section = els.sections[index];
    if (!section) return;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ── BACKGROUND CROSSFADE ──────────────────────────────── */
  function switchBg(toIndex) {
    const bgIndex = BG_MAP[toIndex];
    els.bgs?.forEach((bg, i) => {
      bg.classList.toggle('hep-bg--active', i === bgIndex);
    });
  }

  /* ── UI UPDATE ──────────────────────────────────────────── */
  function updateUI(index) {
    switchBg(index);
    els.dots?.forEach((dot, i) => {
      dot.classList.toggle('hep-dots__dot--active', i === index);
    });
    if (els.arrowDown) {
      els.arrowDown.classList.toggle(
        'hep-arrow-down--hidden',
        index >= CFG.totalSections - 1
      );
    }
  }

  /* ── BINDINGS ───────────────────────────────────────────── */
  function bindDots() {
    els.dots?.forEach(dot => {
      dot.addEventListener('click', () => {
        goTo(parseInt(dot.getAttribute('data-goto'), 10));
      });
    });
  }

  function bindArrow() {
    els.arrowDown?.addEventListener('click', () => goTo(state.current + 1));
  }

  function bindKeyboard() {
    window.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        goTo(state.current + 1);
      }
      if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        goTo(state.current - 1);
      }
    });
  }

  /* ── PUBLIC API ─────────────────────────────────────────── */
  window.HEP = {
    goTo,
    getState  : () => ({ ...state }),
    getCurrent: () => state.current,
  };

  /* ================================================================
     SECTION ANIMATIONS
     Pattern: setHidden() on init + sectionHidden,
              enter()     on sectionEnter
     ================================================================ */

  /* ── S01 · HERO ─────────────────────────────────────────── */
  const S01 = {
    LEFT     : '#hepS01Left',
    ADRIANA  : '#hepS01Adriana',
    THUMBNAIL: '#hepS01Thumbnail',
    DUR : 0.75,
    EASE: 'back.out(1.15)',

    setHidden() {
      gsap.set(this.LEFT,      { scale: 0.85, opacity: 0, transformOrigin: 'left center' });
      gsap.set(this.ADRIANA,   { scale: 0.85, opacity: 0, transformOrigin: 'right center' });
      gsap.set(this.THUMBNAIL, { scale: 0.9,  opacity: 0, transformOrigin: 'center center' });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.LEFT,      { scale: 1, opacity: 1, duration: this.DUR, ease: this.EASE, transformOrigin: 'left center' }, 0);
      tl.to(this.ADRIANA,   { scale: 1, opacity: 1, duration: this.DUR, ease: this.EASE, transformOrigin: 'right center' }, 0.05);
      tl.to(this.THUMBNAIL, { scale: 1, opacity: 1, duration: this.DUR * 0.9, ease: this.EASE, transformOrigin: 'center center' }, 0.15);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS01')) return;
      this.setHidden();

      document.getElementById('hepS01Thumbnail')?.addEventListener('click', () => {
        window.HEP?.goTo(1);
      });

      this.enter(0.3);

      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 0) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 0) this.setHidden();
      });
    }
  };

  /* ── S02 · VIDEO ────────────────────────────────────────── */
  const S02 = {
    LABEL: '#hepS02Label',
    TITLE: '#hepS02Title',
    FRAME: '#hepS02Frame',
    CTA  : '#hepS02Cta',
    DUR : 0.7,
    EASE: 'back.out(1.15)',

    setHidden() {
      gsap.set(this.LABEL, { y: -30, opacity: 0 });
      gsap.set(this.TITLE, { y: -20, opacity: 0 });
      gsap.set(this.FRAME, { y:  50, scale: 0.96, opacity: 0 });
      gsap.set(this.CTA,   { y:  30, opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.LABEL, { y: 0, opacity: 1, duration: this.DUR * 0.8, ease: this.EASE }, 0);
      tl.to(this.TITLE, { y: 0, opacity: 1, duration: this.DUR * 0.9, ease: this.EASE }, 0.05);
      tl.to(this.FRAME, { y: 0, scale: 1,   opacity: 1, duration: this.DUR, ease: this.EASE }, 0.1);
      tl.to(this.CTA,   { y: 0, opacity: 1, duration: this.DUR * 0.8, ease: this.EASE }, 0.2);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS02')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 1) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 1) this.setHidden();
      });
    }
  };

  /* ── S03 · ¿TE HA PASADO ESTO? ─────────────────────────── */
  const S03 = {
    TITLE: '#hepS03Title',
    CARDS: '#hepS03Grid .hep-s03__card',
    DUR : 0.65,
    EASE: 'back.out(1.1)',

    setHidden() {
      gsap.set(this.TITLE, { y: -30, opacity: 0 });
      gsap.set(this.CARDS, { y:  40, opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.TITLE, { y: 0, opacity: 1, duration: this.DUR, ease: this.EASE }, 0);
      tl.to(this.CARDS, { y: 0, opacity: 1, duration: this.DUR, ease: this.EASE, stagger: 0.07 }, 0.1);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS03')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 2) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 2) this.setHidden();
      });
    }
  };

  /* ── S04 · LA REALIDAD ES ESTA ──────────────────────────── */
  const S04 = {
    TITLE: '#hepS04Title',
    DESC : '#hepS04Desc',
    CTA  : '#hepS04Cta',
    DUR : 0.8,
    EASE: 'expo.out',

    setHidden() {
      gsap.set(this.TITLE, { scale: 0.92, opacity: 0 });
      gsap.set(this.DESC,  { y: 24, opacity: 0 });
      gsap.set(this.CTA,   { y: 24, opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.TITLE, { scale: 1, opacity: 1, duration: this.DUR, ease: this.EASE }, 0);
      tl.to(this.DESC,  { y: 0, opacity: 1, duration: this.DUR * 0.85, ease: this.EASE }, 0.2);
      tl.to(this.CTA,   { y: 0, opacity: 1, duration: this.DUR * 0.75, ease: this.EASE }, 0.35);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS04')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 3) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 3) this.setHidden();
      });
    }
  };

  /* ── S05 · TU VOZ PUEDE... ──────────────────────────────── */
  const S05 = {
    KICKER  : '#hepS05Kicker',
    HEADLINE: '#hepS05Headline',
    CTA     : '#hepS05Cta',
    DUR : 0.8,
    EASE: 'expo.out',

    setHidden() {
      gsap.set(this.KICKER,   { y: -20, opacity: 0 });
      gsap.set(this.HEADLINE, { scale: 1.06, opacity: 0 });
      gsap.set(this.CTA,      { y: 28,  opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.KICKER,   { y: 0, opacity: 1, duration: this.DUR * 0.75, ease: this.EASE }, 0);
      tl.to(this.HEADLINE, { scale: 1, opacity: 1, duration: this.DUR, ease: this.EASE }, 0.1);
      tl.to(this.CTA,      { y: 0, opacity: 1, duration: this.DUR * 0.7, ease: this.EASE }, 0.3);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS05')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 4) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 4) this.setHidden();
      });
    }
  };

  /* ── S06 · CERTIFICACIÓN ────────────────────────────────── */
  const S06 = {
    CONTENT: '#hepS06Content',
    VISUAL : '#hepS06Visual',
    DUR : 0.85,
    EASE: 'expo.out',

    setHidden() {
      gsap.set(this.CONTENT, { x: -50, opacity: 0 });
      gsap.set(this.VISUAL,  { x:  50, opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.CONTENT, { x: 0, opacity: 1, duration: this.DUR, ease: this.EASE }, 0);
      tl.to(this.VISUAL,  { x: 0, opacity: 1, duration: this.DUR, ease: this.EASE }, 0.1);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS06')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 5) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 5) this.setHidden();
      });
    }
  };

  /* ── S07 · ADRIANA GALLARDO ─────────────────────────────── */
  const S07 = {
    CONTENT: '#hepS07Content',
    GALLERY: '#hepS07Gallery',
    DUR : 0.85,
    EASE: 'expo.out',

    setHidden() {
      gsap.set(this.CONTENT, { x: -50, opacity: 0 });
      gsap.set(this.GALLERY, { x:  50, opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.CONTENT, { x: 0, opacity: 1, duration: this.DUR, ease: this.EASE }, 0);
      tl.to(this.GALLERY, { x: 0, opacity: 1, duration: this.DUR, ease: this.EASE }, 0.1);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS07')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 6) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 6) this.setHidden();
      });
    }
  };

  /* ── S08 · LO QUE PUEDE CAMBIAR ─────────────────────────── */
  const S08 = {
    TITLE: '#hepS08Title',
    CARDS: '#hepS08Comparison .hep-s08__card',
    ARROW: '#hepS08Arrow',
    DUR : 0.75,
    EASE: 'back.out(1.1)',

    setHidden() {
      gsap.set(this.TITLE, { y: -30, opacity: 0 });
      gsap.set(this.CARDS, { y:  40, scale: 0.95, opacity: 0 });
      gsap.set(this.ARROW, { scale: 0.7, opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.TITLE, { y: 0, opacity: 1, duration: this.DUR, ease: this.EASE }, 0);
      tl.to(this.CARDS, { y: 0, scale: 1, opacity: 1, duration: this.DUR, ease: this.EASE, stagger: 0.12 }, 0.1);
      tl.to(this.ARROW, { scale: 1, opacity: 1, duration: this.DUR * 0.7, ease: this.EASE }, 0.2);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS08')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 7) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 7) this.setHidden();
      });
    }
  };

  /* ── S09 · LO QUE APRENDERÁS ────────────────────────────── */
  const S09 = {
    TITLE: '#hepS09Title',
    CARDS: '#hepS09Grid .hep-s09__card',
    DUR : 0.65,
    EASE: 'back.out(1.1)',

    setHidden() {
      gsap.set(this.TITLE, { y: -30, opacity: 0 });
      gsap.set(this.CARDS, { y:  40, opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.TITLE, { y: 0, opacity: 1, duration: this.DUR, ease: this.EASE }, 0);
      tl.to(this.CARDS, { y: 0, opacity: 1, duration: this.DUR, ease: this.EASE, stagger: 0.07 }, 0.1);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS09')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 8) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 8) this.setHidden();
      });
    }
  };

  /* ── S10 · EXPERIENCIA TRANSFORMADORA ────────────────────── */
  const S10 = {
    TITLE: '#hepS10Title',
    CARD : '#hepS10Card',
    CTA  : '#hepS10Cta',
    DUR : 0.85,
    EASE: 'expo.out',

    setHidden() {
      gsap.set(this.TITLE, { y: -30, opacity: 0 });
      gsap.set(this.CARD,  { y:  40, scale: 0.95, opacity: 0 });
      gsap.set(this.CTA,   { y:  20, opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.TITLE, { y: 0, opacity: 1, duration: this.DUR * 0.75, ease: this.EASE }, 0);
      tl.to(this.CARD,  { y: 0, scale: 1, opacity: 1, duration: this.DUR, ease: this.EASE }, 0.1);
      tl.to(this.CTA,   { y: 0, opacity: 1, duration: this.DUR * 0.7, ease: this.EASE }, 0.3);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS10')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 9) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 9) this.setHidden();
      });
    }
  };

  /* ── S11 · TESTIMONIOS ──────────────────────────────────── */
  const S11 = {
    TITLE : '#hepS11Title',
    MAIN  : '#hepS11MainVideo',
    THUMBS: '#hepS11Grid .hep-s11__thumb',
    CTA   : '#hepS11Cta',
    DUR : 0.85,
    EASE: 'expo.out',

    setHidden() {
      gsap.set(this.TITLE,  { y: -30, opacity: 0 });
      gsap.set(this.MAIN,   { x: -40, opacity: 0 });
      gsap.set(this.THUMBS, { y:  30, scale: 0.94, opacity: 0 });
      gsap.set(this.CTA,    { y:  20, opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.TITLE,  { y: 0, opacity: 1, duration: this.DUR * 0.7, ease: this.EASE }, 0);
      tl.to(this.MAIN,   { x: 0, opacity: 1, duration: this.DUR, ease: this.EASE }, 0.1);
      tl.to(this.THUMBS, { y: 0, scale: 1, opacity: 1, duration: this.DUR * 0.8, ease: this.EASE, stagger: 0.1 }, 0.2);
      tl.to(this.CTA,    { y: 0, opacity: 1, duration: this.DUR * 0.6, ease: this.EASE }, 0.5);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS11')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 10) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 10) this.setHidden();
      });

      const grid = document.getElementById('hepS11Grid');
      const mainVideo = document.getElementById('hepS11MainVideo');
      if (!grid || !mainVideo) return;

      grid.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-video-url]');
        if (!thumb) return;

        const videoEl = mainVideo.querySelector('[data-vp-video]');
        const sourceEl = videoEl?.querySelector('source');
        const player = mainVideo.querySelector('[data-vp]');
        if (!videoEl || !sourceEl) return;

        videoEl.pause();
        player?.classList.remove('is-playing');
        sourceEl.src = thumb.dataset.videoUrl;
        if (thumb.dataset.posterUrl) videoEl.poster = thumb.dataset.posterUrl;
        videoEl.load();

        grid.querySelectorAll('[data-video-url]').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    }
  };

  /* ── S12 · PRICING ──────────────────────────────────────── */
  const S12 = {
    HEADER: '#hepS12Header',
    CARDS : '#hepS12Cards .hep-s12__card',
    FOOTER: '#hepS12Footer',
    DUR : 0.85,
    EASE: 'expo.out',

    setHidden() {
      gsap.set(this.HEADER, { y: -30, opacity: 0 });
      gsap.set(this.CARDS,  { y:  40, scale: 0.93, opacity: 0 });
      gsap.set(this.FOOTER, { y:  20, opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.HEADER, { y: 0, opacity: 1, duration: this.DUR * 0.75, ease: this.EASE }, 0);
      tl.to(this.CARDS,  { y: 0, scale: 1, opacity: 1, duration: this.DUR, ease: this.EASE, stagger: 0.12 }, 0.1);
      tl.to(this.FOOTER, { y: 0, opacity: 1, duration: this.DUR * 0.7, ease: this.EASE }, 0.35);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS12')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 11) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 11) this.setHidden();
      });
    }
  };

  /* ── S13 · FINAL CTA ────────────────────────────────────── */
  const S13 = {
    LINE1 : '#hepS13Title .hep-s13__title-line1',
    LINE2 : '#hepS13Title .hep-s13__title-line2',
    PHOTO : '#hepS13Photo',
    BOTTOM: '#hepS13Bottom',
    DUR : 0.9,
    EASE: 'expo.out',

    setHidden() {
      gsap.set(this.LINE1,  { y: -50, opacity: 0 });
      gsap.set(this.LINE2,  { y: -50, opacity: 0 });
      gsap.set(this.PHOTO,  { scale: 1.06, opacity: 0 });
      gsap.set(this.BOTTOM, { y: 30, opacity: 0 });
    },

    enter(delay = 0) {
      const tl = gsap.timeline({ delay });
      tl.to(this.LINE1,  { y: 0, opacity: 1, duration: this.DUR * 0.7, ease: this.EASE }, 0);
      tl.to(this.LINE2,  { y: 0, opacity: 1, duration: this.DUR * 0.7, ease: this.EASE }, 0.08);
      tl.to(this.PHOTO,  { scale: 1, opacity: 1, duration: this.DUR, ease: this.EASE }, 0.1);
      tl.to(this.BOTTOM, { y: 0, opacity: 1, duration: this.DUR * 0.75, ease: this.EASE }, 0.28);
      return tl;
    },

    init() {
      if (!document.getElementById('hepS13')) return;
      this.setHidden();
      document.addEventListener('hep:sectionEnter', (e) => {
        if (e.detail.index === 12) this.enter(0.05);
      });
      document.addEventListener('hep:sectionHidden', (e) => {
        if (e.detail.index === 12) this.setHidden();
      });
    }
  };

  /* ── BOOTSTRAP ──────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    init();
    S01.init();
    S02.init();
    S03.init();
    S04.init();
    S05.init();
    S06.init();
    S07.init();
    S08.init();
    S09.init();
    S10.init();
    S11.init();
    S12.init();
    S13.init();
  });

}());
